
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../../components/base/Navigation';
import Footer from '../../components/base/Footer';

export default function Home() {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const words = ['Luxury', 'Quality', 'Excellence', 'Innovation'];
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Evolution Home - Luxury Home Renovations | 350+ Projects Completed';
    const interval = setInterval(() => {
      setCurrentWordIndex((prev) => (prev + 1) % words.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const stats = [
    { number: '350+', label: 'Projects Completed', icon: 'ri-home-line' },
    { number: '6+', label: 'Years Experience', icon: 'ri-time-line' },
    { number: '350+', label: 'Happy Clients', icon: 'ri-user-smile-line' },
    { number: '15+', label: 'Expert Craftsmen', icon: 'ri-hammer-line' },
  ];

  const services = [
    { name: 'Kitchen Renovations', icon: 'ri-restaurant-line' },
    { name: 'Bathroom Remodeling', icon: 'ri-drop-line' },
    { name: 'Flooring', icon: 'ri-layout-grid-line' },
    { name: 'Tiling', icon: 'ri-grid-line' },
    { name: 'Painting', icon: 'ri-brush-line' },
    { name: 'Plumbing', icon: 'ri-tools-line' },
    { name: 'Electrical', icon: 'ri-flashlight-line' },
    { name: 'Commercial Units', icon: 'ri-building-line' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section 
        className="relative h-screen flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://public.readdy.ai/ai/img_res/4daf5e0a26380a93950065f43933005b.jpg')`
        }}
      >
        <div className="w-full text-center text-white px-4">
          <div className="max-w-4xl mx-auto">
            {/* Prominent Logo in Hero */}
            <div className="mb-8">
              <img 
                src="https://static.readdy.ai/image/9d1a81acaff8e51df20fb8b8c1aae197/4aa7700c01a2a06640654e7632c5fa8b.png" 
                alt="Evolution Home Renovations" 
                className="h-32 md:h-40 w-auto object-contain mx-auto animate-pulse-slow"
              />
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              Redefining{' '}
              <span className="text-red-600 transition-all duration-500">
                {words[currentWordIndex]}
              </span>
              <br />in Home Renovation
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-200">
              Transform your space with Evolution Home's premium renovation services
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                className="px-8 py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-all duration-300 whitespace-nowrap cursor-pointer"
                onClick={() => navigate('/contact')}
              >
                Start Your Project
              </button>
              <button 
                className="px-8 py-4 border-2 border-red-600 text-red-600 font-semibold rounded-lg hover:bg-red-600 hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
                onClick={() => navigate('/gallery')}
              >
                View Gallery
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee/Ticker */}
      <div className="bg-red-600 py-3 overflow-hidden">
        <div className="animate-marquee whitespace-nowrap">
          <span className="text-white font-semibold text-lg mx-8">
            ★ PREMIUM RENOVATIONS ★ LUXURY FINISHES ★ EXPERT CRAFTSMANSHIP ★ 350+ COMPLETED PROJECTS ★ 
            PREMIUM RENOVATIONS ★ LUXURY FINISHES ★ EXPERT CRAFTSMANSHIP ★ 350+ COMPLETED PROJECTS ★
          </span>
        </div>
      </div>

      {/* Stats Section */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="w-16 h-16 mx-auto mb-4 bg-red-600/10 rounded-full flex items-center justify-center group-hover:bg-red-600/20 transition-all duration-300">
                  <i className={`${stat.icon} text-2xl text-red-600`}></i>
                </div>
                <div className="text-4xl font-bold text-red-600 mb-2">{stat.number}</div>
                <div className="text-white text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Pills */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Our Premium Services</h2>
            <p className="text-xl text-gray-600">Excellence in every detail, luxury in every project</p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4">
            {services.map((service, index) => (
              <div 
                key={index}
                className="flex items-center space-x-3 px-6 py-3 bg-white border-2 border-red-600/20 rounded-full hover:border-red-600 hover:bg-red-50 transition-all duration-300 cursor-pointer group"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${service.icon} text-red-600 group-hover:scale-110 transition-transform duration-300`}></i>
                </div>
                <span className="font-medium text-gray-800">{service.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section - New Design */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-4">Our Process</h2>
            <p className="text-xl text-gray-600">From concept to completion in 4 simple steps</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { 
                step: '01', 
                title: 'Consultation', 
                desc: 'We discuss your vision and requirements in detail',
                icon: 'ri-chat-4-line'
              },
              { 
                step: '02', 
                title: 'Design', 
                desc: 'Our experts create detailed plans and 3D visualizations',
                icon: 'ri-pencil-ruler-2-line'
              },
              { 
                step: '03', 
                title: 'Construction', 
                desc: 'Professional execution with premium materials',
                icon: 'ri-hammer-line'
              },
              { 
                step: '04', 
                title: 'Completion', 
                desc: 'Final walkthrough and project handover',
                icon: 'ri-checkbox-circle-line'
              }
            ].map((item, index) => (
              <div 
                key={index} 
                className="relative bg-gray-50 p-8 rounded-xl hover:shadow-xl transition-all duration-300 group border-2 border-transparent hover:border-red-600"
              >
                <div className="absolute -top-4 -right-4 w-12 h-12 bg-red-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                  {item.step}
                </div>
                
                <div className="w-16 h-16 bg-red-600/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-red-600 transition-all duration-300">
                  <i className={`${item.icon} text-3xl text-red-600 group-hover:text-white transition-all duration-300`}></i>
                </div>
                
                <h3 className="text-xl font-semibold text-black mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.desc}</p>
                
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div 
                    className="flex items-center text-red-600 font-medium group-hover:translate-x-2 transition-transform duration-300 cursor-pointer"
                    onClick={() => navigate('/contact')}
                  >
                    <span className="text-sm">Learn More</span>
                    <div className="w-4 h-4 flex items-center justify-center ml-2">
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-6">Ready to get started with your renovation project?</p>
            <button 
              className="px-8 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-all duration-300 whitespace-nowrap cursor-pointer"
              onClick={() => navigate('/contact')}
            >
              Schedule Free Consultation
            </button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          {/* Logo in CTA */}
          <div className="mb-8">
            <img 
              src="https://static.readdy.ai/image/9d1a81acaff8e51df20fb8b8c1aae197/4aa7700c01a2a06640654e7632c5fa8b.png" 
              alt="Evolution Home Renovations" 
              className="h-24 w-auto object-contain mx-auto opacity-90"
            />
          </div>
          
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Transform Your Space?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Join over 350 satisfied clients who have experienced Evolution Home's premium renovation services
          </p>
          <button 
            className="px-10 py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-all duration-300 whitespace-nowrap cursor-pointer text-lg"
            onClick={() => navigate('/contact')}
          >
            Get Your Free Consultation
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
